import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import logger from '../utils/logger';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = 'urbanik_token';

// Use sessionStorage instead of localStorage to mitigate XSS-based token theft.
// Tokens persist only for the current tab session, reducing attack surface.
const tokenStorage = {
  get: () => {
    try { return sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY); }
    catch (e) { logger.error('Token read failed:', e); return null; }
  },
  set: (v) => {
    try {
      sessionStorage.setItem(TOKEN_KEY, v);
      // Clear any legacy localStorage token from previous versions
      localStorage.removeItem(TOKEN_KEY);
    } catch (e) { logger.error('Token storage failed:', e); }
  },
  remove: () => {
    try { sessionStorage.removeItem(TOKEN_KEY); localStorage.removeItem(TOKEN_KEY); }
    catch (e) { logger.error('Token remove failed:', e); }
  },
};

const sanitizeToken = (token) => {
  if (!token || typeof token !== 'string') return null;
  if (!/^[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+$/.test(token)) return null;
  return token;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => sanitizeToken(tokenStorage.get()));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          // Only clear token on auth-specific failures (401/403), not on transient network/server errors
          const status = error?.response?.status;
          if (status === 401 || status === 403) {
            logger.warn('Auth token rejected (status', status, '), clearing.');
            tokenStorage.remove();
            setToken(null);
            setUser(null);
          } else {
            // Transient error: keep token, allow user to retry. Log only.
            logger.error('Auth init temporary error (token preserved):', error?.message || error);
          }
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  // Global axios interceptor: handle 401 from any API call by clearing stale auth.
  // Mounted once at provider level so user actions auto-recover from expired tokens.
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (res) => res,
      (error) => {
        const status = error?.response?.status;
        const url = error?.config?.url || '';
        // Only react to 401/403 from our own API; ignore login attempts (they handle errors locally)
        if ((status === 401 || status === 403) && url.includes('/api/') && !url.includes('/auth/login')) {
          if (token) {
            logger.warn('API returned', status, '— clearing stale auth token');
            tokenStorage.remove();
            setToken(null);
            setUser(null);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptorId);
  }, [token]);

  const login = (accessToken, userData) => {
    const clean = sanitizeToken(accessToken);
    if (!clean) return null;
    tokenStorage.set(clean);
    setToken(clean);
    setUser(userData);
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { access_token, user: newUser } = response.data;
    const clean = sanitizeToken(access_token);
    if (clean) {
      tokenStorage.set(clean);
      setToken(clean);
    }
    setUser(newUser);
    return newUser;
  };

  const logout = () => {
    tokenStorage.remove();
    setToken(null);
    setUser(null);
  };

  const getAuthHeader = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, getAuthHeader, isAdmin: user?.is_admin }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
